import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { BookOpen, Clock, CheckCircle, XCircle, Award } from 'lucide-react';
import Header from './Header';

// Using relative URLs

const TrainingTake = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchTraining();
  }, [trainingId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTraining = async () => {
    try {
      const response = await axios.get(
        `/api/trainings/${trainingId}`,
        getAuthHeaders()
      );
      setTraining(response.data);
    } catch (error) {
      toast.error('Failed to load training');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const handleSubmit = async () => {
    // Check all questions are answered
    const allAnswered = training.questions.every(q => answers[q.id]);
    
    if (!allAnswered) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setLoading(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await axios.post(
        `/api/trainings/${trainingId}/submit`,
        { answers: formattedAnswers },
        getAuthHeaders()
      );

      setResult(response.data);
      setSubmitted(true);
      
      if (response.data.passed) {
        toast.success('Congratulations! You passed!');
      } else {
        toast.error('Please review and try again');
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already completed')) {
        toast.error('You have already completed this training');
        navigate('/employee/portal');
      } else {
        toast.error('Failed to submit training');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Training Not Found</h2>
          <Button onClick={() => navigate('/employee/portal')}>Back to Portal</Button>
        </div>
      </div>
    );
  }

  // Result view after submission
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className={`text-center ${result.passed ? 'border-navy-500' : 'border-orange-500'}`}>
            <CardHeader>
              {result.passed ? (
                <>
                  <Award className="w-16 h-16 mx-auto mb-4 text-navy-600" />
                  <CardTitle className="text-3xl text-navy-600">Congratulations!</CardTitle>
                  <CardDescription className="text-lg">You passed the training!</CardDescription>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 mx-auto mb-4 text-orange-600" />
                  <CardTitle className="text-3xl text-orange-600">Keep Learning!</CardTitle>
                  <CardDescription className="text-lg">Review the material and try again</CardDescription>
                </>
              )}
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{result.score}%</p>
                  <p className="text-sm text-slate-600">Score</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-navy-600">{result.correct_answers}</p>
                  <p className="text-sm text-slate-600">Correct</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{result.total_questions}</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-slate-700">{result.message}</p>
              </div>

              <Button
                onClick={() => navigate('/employee/portal')}
                className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
              >
                Back to Portal
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Training content and quiz view
  const questions = training.questions || [];
  const progress = questions.length > 0 ? ((Object.keys(answers).length / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-navy-600" />
                  <Badge className="bg-emerald-100 text-emerald-800">
                    Training
                  </Badge>
                </div>
                <CardTitle className="text-2xl mb-2">{training.title}</CardTitle>
                {training.description && (
                  <CardDescription className="text-base">
                    {training.description}
                  </CardDescription>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 mt-4">
              {training.frameworks && training.frameworks.map((fw) => (
                <Badge key={fw} variant="outline">{fw}</Badge>
              ))}
              <div className="flex items-center text-sm text-slate-600">
                <Clock className="w-4 h-4 mr-1" />
                {training.duration_minutes} minutes
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Training Content */}
        {training.content && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Training Material</CardTitle>
              <CardDescription>Read this carefully before taking the quiz</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">{training.content}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="mb-2 flex justify-between text-sm">
              <span className="text-slate-600">Progress</span>
              <span className="text-slate-900 font-medium">
                {Object.keys(answers).length}/{questions.length} answered
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-navy-600 h-2 rounded-full transition-all"
                style={{width: `${progress}%`}}
              ></div>
            </div>
          </CardContent>
        </Card>

        {/* Quiz Questions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Quiz Questions</CardTitle>
            <CardDescription>Answer all questions to complete the training</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id} className="p-4 border rounded-lg bg-white">
                <p className="font-semibold text-slate-900 mb-4">
                  {index + 1}. {question.question}
                </p>
                <div className="space-y-2">
                  {Object.entries(question.options).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleAnswerSelect(question.id, key)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        answers[question.id] === key
                          ? 'border-navy-600 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="font-medium mr-2">{key}.</span>
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/employee/portal')}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || Object.keys(answers).length !== questions.length}
            className="flex-1 bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {loading ? 'Submitting...' : 'Submit Training'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainingTake;
